import BigNumber from 'bignumber.js';
import { useEffect, useMemo } from 'react';
import { Controller, useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import FlowPaymentMethodSelect from '~/components/aside/FlowDetails/FlowPaymentMethodSelect';
import AlertMessage from '~/components/data-display/AlertMessage';
import BankAccountSelect from '~/components/inputs/Select/BankAccountSelect';
import TextField from '~/components/inputs/TextField/TextField';
import Text from '~/components/typographies/Text';
import { useValuesTotals } from '~/hooks/document-builder/useValuesTotals';
import { useAccountQuery } from '~/services/account';
import { useInvoicingSettings } from '~/services/company-settings';
import { useCreateInvoiceFlow, useDeleteInvoiceFlow } from '~/services/invoicing';
import { EInvoiceType } from '~/types/document';
import { DocumentBuilderFlowForm, DocumentBuilderForm } from '~/types/document-builder';
import { FlowPaymentStatus, PaymentMethod } from '~/types/flow';
import { isDefined } from '~/types/typeGuards';
import { resolveAccountIdentifier } from '~/utils/accounts';
import { isConsentDisabled } from '~/utils/consents';
import { createEmptyFlow } from '~/utils/document-builder';
import { formatMoney } from '~/utils/money';
import AddControlButton from '~/views/invoices/builder/AdditionalControlButton';
import BuilderSection from '~/views/invoices/builder/BuilderSection';
import FlowForm from '~/views/invoices/builder/flows/FlowForm';
import VatCollectionSelect from '~/views/invoices/builder/flows/VatCollectionSelect';
import { usePresetDates } from '~/views/invoices/builder/hooks/presetDates';
import { useVatCollectionState } from '~/views/invoices/builder/hooks/sections';
import styles from './FlowsSection.module.scss';

export const excludedPaymentMethods: PaymentMethod[] = [
  PaymentMethod.Other,
  PaymentMethod.FiscalCredit,
  PaymentMethod.Deferred,
  PaymentMethod.TaxForm,
];

const PaymentInfoSection = () => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });

  const { control, watch } = useFormContext<DocumentBuilderForm>();
  const {
    field: { onChange: paymentAccountOnChange },
  } = useController({ control, name: 'paymentInfo.paymentAccount' });
  const {
    field: { onChange: institutionOnChange },
  } = useController({ control, name: 'paymentInfo.institutionName' });

  const [vatCollectionVisible, showVatCollection] = useVatCollectionState();

  const accountId = watch('paymentInfo.accountId');
  const accountQuery = useAccountQuery(accountId);
  const identifier = (isDefined(accountQuery.data) && resolveAccountIdentifier(accountQuery.data)) || null;
  const institution = accountQuery.data?.consent.institution?.name ?? null;

  useEffect(() => {
    if (accountId && identifier) {
      paymentAccountOnChange(identifier.value);
    }
  }, [accountId, identifier, paymentAccountOnChange]);

  useEffect(() => {
    if (accountId && institution) {
      institutionOnChange(institution);
    }
  }, [accountId, institution, institutionOnChange]);

  const showCoordinatesField = !identifier;

  return (
    <BuilderSection title={t('sections.payment_info')}>
      <div className={styles.row}>
        <Text variant="body1" weight="semibold" className={styles.label}>
          {t('fields.payment_method.label')}
        </Text>
        <div className={styles.fieldContainer}>
          <Controller
            control={control}
            name="paymentInfo.paymentMethod"
            render={({ field, fieldState }) => (
              <FlowPaymentMethodSelect
                {...field}
                errorMessage={fieldState.error?.message}
                excludedPaymentMethods={excludedPaymentMethods}
                hideLabel
                isInvalid={fieldState.invalid}
                label={t('fields.payment_method.label')}
              />
            )}
          />
        </div>
      </div>
      <div className={styles.row}>
        <Text variant="body1" weight="semibold" className={styles.label}>
          {t('fields.account.label')}
        </Text>
        <div className={styles.fieldContainer}>
          <Controller
            control={control}
            name="paymentInfo.accountId"
            render={({ field, fieldState }) => (
              <BankAccountSelect
                {...field}
                emptyOption="Altro conto (non connesso a Sibill)"
                errorMessage={fieldState.error?.message}
                filterAccount={(account) => !isConsentDisabled(account.consent)}
                hideLabel
                label={t('fields.account.label')}
                isInvalid={fieldState.invalid}
              />
            )}
          />
        </div>
      </div>
      {showCoordinatesField && (
        <div className={styles.row}>
          <Text variant="body1" weight="semibold" className={styles.label}>
            {t('fields.payment_account.label')}
          </Text>
          <div className={styles.fieldContainer}>
            <Controller
              control={control}
              name="paymentInfo.paymentAccount"
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  errorMessage={fieldState.error?.message}
                  hideLabel
                  isInvalid={fieldState.invalid}
                  label={t('fields.payment_account.label')}
                />
              )}
            />
          </div>
        </div>
      )}
      {vatCollectionVisible && (
        <div className={styles.row}>
          <Text variant="body1" weight="semibold" className={styles.label}>
            {t('fields.vat_collection.label')}
          </Text>
          <div className={styles.fieldContainer}>
            <Controller
              control={control}
              name="vatCollection"
              render={({ field, fieldState }) => (
                <VatCollectionSelect
                  {...field}
                  errorMessage={fieldState.error?.message}
                  hideLabel
                  isInvalid={fieldState.invalid}
                  label={t('fields.vat_collection.label')}
                />
              )}
            />
          </div>
        </div>
      )}
      {!vatCollectionVisible && (
        <div>
          <AddControlButton onClick={showVatCollection}>{t('controls.add_vat_collection')}</AddControlButton>
        </div>
      )}
    </BuilderSection>
  );
};

const FlowBuilderAlerts = ({ flows, currency }: { flows: DocumentBuilderFlowForm[]; currency: string }) => {
  const invoiceTotals = useValuesTotals();
  const flowsTotal = flows.reduce((prev, curr) => prev.plus(BigNumber(curr.amount)), BigNumber(0)).decimalPlaces(2);
  const invoiceTotal = BigNumber(invoiceTotals.totalToPay.amount).decimalPlaces(2);
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  const positiveDifference = flowsTotal.isGreaterThan(invoiceTotal)
    ? formatMoney({ amount: flowsTotal.minus(invoiceTotal).toString(), currency })
    : null;

  const negativeDifference = flowsTotal.isLessThan(invoiceTotal)
    ? formatMoney({ amount: invoiceTotal.minus(flowsTotal).toString(), currency })
    : null;

  const [debouncedPositiveDiff] = useDebounce(positiveDifference, 500);
  const [debouncedNegativeDiff] = useDebounce(negativeDifference, 500);

  if (debouncedPositiveDiff)
    return (
      <AlertMessage type="warning" box>
        {t('alerts.warning_price_mismatch_more', { positiveDifference: debouncedPositiveDiff })}
      </AlertMessage>
    );
  if (debouncedNegativeDiff)
    return (
      <AlertMessage type="warning" box>
        {t('alerts.warning_price_mismatch_less', { negativeDifference: debouncedNegativeDiff })}
      </AlertMessage>
    );

  return null;
};

const FlowBuilderSection: React.FC<{ invoiceId?: string }> = ({ invoiceId }) => {
  const { t } = useTranslation('documents', { keyPrefix: 'builder' });
  const { payment } = useInvoicingSettings();

  const { mutateAsync: createFlow } = useCreateInvoiceFlow();
  const { mutateAsync: deleteFlow } = useDeleteInvoiceFlow();

  const { control, watch, getValues, setValue } = useFormContext<DocumentBuilderForm>();
  const { fields, append, remove } = useFieldArray({ control, name: 'flows' });

  const presetDates = usePresetDates();

  const [counterpart, currency, flows, paymentInfo, subtype] = watch([
    'counterpart',
    'currency',
    'flows',
    'paymentInfo',
    'subtype',
  ]);

  useEffect(() => {
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValue(
          `flows.${index}.paymentStatus`,
          subtype === EInvoiceType.AutofatturaPerAutoconsumo
            ? FlowPaymentStatus.Paid
            : (fields[index]?.paymentStatus ?? FlowPaymentStatus.ToPay),
          { shouldDirty: true },
        );
      });
    }
  }, [subtype, fields, setValue]);

  const handleAddLine = async () => {
    const flow = createEmptyFlow(getValues('date'), payment, counterpart);
    if (isDefined(invoiceId)) {
      const { id: remoteId } = await createFlow({
        flow,
        paymentInfo,
        invoiceId,
        currency,
      });
      append({ ...flow, remoteId });
    } else {
      append(flow);
    }
  };

  const handleRemoveLine = async (remoteId: string | null, flowIndex: number) => {
    if (isDefined(remoteId)) {
      await deleteFlow(remoteId);
    }
    setTimeout(() => remove(flowIndex));
  };

  useEffect(() => {
    if (counterpart?.paymentMethod) setValue('paymentInfo.paymentMethod', counterpart.paymentMethod);
  }, [counterpart?.paymentMethod, setValue]);

  const isFormDisabled = useMemo(() => {
    return subtype === EInvoiceType.AutofatturaPerAutoconsumo;
  }, [subtype]);

  return (
    <BuilderSection title={t('sections.flows')}>
      {fields.map((flow, index) => (
        <FlowForm
          key={flow.id}
          index={index}
          currency={currency}
          canDelete={fields.length > 1}
          onDelete={() => handleRemoveLine(flow.remoteId, index)}
          isDisabled={isFormDisabled}
          presetDates={presetDates}
        />
      ))}
      <FlowBuilderAlerts flows={flows} currency={currency} />
      <div>
        <AddControlButton onClick={handleAddLine} disabled={isFormDisabled}>
          {t('controls.add_deadline')}
        </AddControlButton>
      </div>
    </BuilderSection>
  );
};

const FlowsSection: React.FC<{ invoiceId?: string }> = ({ invoiceId }) => {
  return (
    <div className={styles.container}>
      <PaymentInfoSection />
      <FlowBuilderSection invoiceId={invoiceId} />
    </div>
  );
};

export default FlowsSection;
